import React, { useMemo, useState } from 'react';
import { Search, Plus, MoreHorizontal, Mail, Users } from 'lucide-react';
import { PEOPLE, ORGANISATION, type DemoPerson } from '../data/demoOrganisation';
type BodyFilter = 'all' | DemoPerson['body'];
const INVITED = new Set(['p12']);
export function TeamDirectory() {
  const [search, setSearch] = useState('');
  const [body, setBody] = useState<BodyFilter>('all');
  const people = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PEOPLE.filter(p => {
      if (body !== 'all' && p.body !== body) return false;
      if (!q) return true;
      return [p.name, p.title, p.email, ...p.committees].join(' ').toLowerCase().includes(q);
    });
  }, [search, body]);
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2348" data-mp="3ceqvfu42w98g">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4" data-id="element-2349" data-mp="wo4mt4v3czpb">
        <div data-id="element-2350" data-mp="rqcptwflane9">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2351" data-mp="rot77z6go09b">Directory</h1>
          <p className="text-slate-500 mt-1" data-id="element-2352" data-mp="39vvr1vj8yo76">
            Board members and executives at {ORGANISATION.name}, with the
            framework role each is assessed in.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center shrink-0" data-id="element-2353" data-mp="3ivisghbz7u08">
          <Plus className="h-4 w-4 mr-2" data-id="element-2354" data-mp="3nt144fhdonbx" />
          Add Person
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2355" data-mp="2q96fxeb6oxai">
        <div className="relative flex-1 max-w-md" data-id="element-2356" data-mp="2v6zizyh6xb6k">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-2357" data-mp="305syfwg1fc9d" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search directory" placeholder="Search by name, role, or committee..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-200" data-id="element-2358" data-mp="303pyajl682qx" />
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50" role="group" aria-label="Filter by body" data-id="element-2359" data-mp="24cweseup5x9u">
          {(['all', 'Board', 'Executive Committee'] as BodyFilter[]).map(b => <button key={b} onClick={() => setBody(b)} aria-pressed={body === b} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${body === b ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-2360" data-mp="297acinwu0gho">
              {b === 'all' ? 'All' : b}
            </button>)}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-id="element-2361" data-mp="1qdwqj8b3jke7">
        {people.length === 0 ? <div className="py-16 text-center" data-id="element-2362" data-mp="36vkhnbrwzkd0">
            <Users className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-2363" data-mp="31tldhzocojf0" />
            <p className="text-sm font-medium text-slate-900" data-id="element-2364" data-mp="nn59b226rycj">No people found</p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-2365" data-mp="np8a8h4vnbw1">
              Try a different search or filter.
            </p>
          </div> : <table className="w-full text-left border-collapse min-w-[720px]" data-id="element-2366" data-mp="en8s1li4m1ri">
            <thead data-id="element-2367" data-mp="jks72nmneiki">
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-medium" data-id="element-2368" data-mp="jmbls9snolk6">
                <th className="p-4" data-id="element-2369" data-mp="17bo2rvu85xx1">Person</th>
                <th className="p-4" data-id="element-2370" data-mp="1bczlriilfkw9">Assessed role</th>
                <th className="p-4" data-id="element-2371" data-mp="1bekh3acqf7v6">Body</th>
                <th className="p-4" data-id="element-2372" data-mp="xll4bg4c3rrb">Committees</th>
                <th className="p-4" data-id="element-2373" data-mp="xkt946enfw1k">Status</th>
                <th className="p-4 text-right" data-id="element-2374" data-mp="12jmipnqtf2wg">
                  <span className="sr-only" data-id="element-2375" data-mp="12ibks4k1wpd9">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-sm" data-id="element-2376" data-mp="1q7w2kk4gxgky">
              {people.map(p => {
            const invited = INVITED.has(p.id);
            return <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-id="element-2377" data-mp="6fxo0066mxdp">
                    <td className="p-4" data-id="element-2378" data-mp="1ero9ie3u9uo">
                      <div className="flex items-center" data-id="element-2379" data-mp="3tjc5o3dtvy01">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3 shrink-0" data-id="element-2380" data-mp="3tjvn7izkgkdj">
                          {p.initials}
                        </div>
                        <div data-id="element-2381" data-mp="p6yioc09s6rg">
                          <div className="font-medium text-slate-900" data-id="element-2382" data-mp="l30jk1ay2vye">{p.name}</div>
                          <div className="text-xs text-slate-500 flex items-center mt-0.5" data-id="element-2383" data-mp="gctw44s65hhq">
                            <Mail className="h-3 w-3 mr-1" data-id="element-2384" data-mp="bdqr2kc6vb9r" />
                            {p.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-700" data-id="element-2385" data-mp="142o9qokbba31">{p.title}</td>
                    <td className="p-4 text-slate-600" data-id="element-2386" data-mp="140bghf93tdao">{p.body}</td>
                    <td className="p-4" data-id="element-2387" data-mp="1beuib1zxyhmi">
                      {p.committees.length === 0 ? <span className="text-slate-400" data-id="element-2388" data-mp="1gdp1w65qga7o">—</span> : <div className="flex flex-wrap gap-1" data-id="element-2389" data-mp="1lanrcbegbszj">
                          {p.committees.map(c => <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600" data-id="element-2390" data-mp="1pevjkik4br2i">
                              {c}
                            </span>)}
                        </div>}
                    </td>
                    <td className="p-4" data-id="element-2391" data-mp="2909oyuf6e05f">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invited ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-id="element-2392" data-mp="293dzsan5bu52">
                        {invited ? 'Invited' : 'Active'}
                      </span>
                    </td>
                    <td className="p-4 text-right" data-id="element-2393" data-mp="dyxxjkh5wtoi">
                      <button aria-label={`Actions for ${p.name}`} className="text-slate-400 hover:text-slate-600" data-id="element-2394" data-mp="dwbofgemi646">
                        <MoreHorizontal className="h-5 w-5" data-id="element-2395" data-mp="mwqc2mf27xau" />
                      </button>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>}
        <div className="p-4 border-t border-slate-200 bg-slate-50 text-sm text-slate-500" data-id="element-2396" data-mp="wqz0axx25437">
          Showing {people.length} of {PEOPLE.length} people
        </div>
      </div>
    </div>;
}